import { useEffect, useState } from "react";
import { Loader2, Users, MailPlus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { CustomerEditor, ACCOUNT_TYPE_LABELS, PLAN_LABELS } from "@/components/CustomerEditor";

const CustomerRow = ({ customer: c, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/customers/${c.id}/invite`);
      toast.success(`Invite sent to ${c.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const save = async (payload) => {
    setBusy(true);
    try {
      const { email, ...updates } = payload;
      await api.patch(`/admin/customers/${c.id}`, updates);
      toast.success("Customer updated");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${c.name} (${c.email}) and all their reports and records? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success(`${c.name} deleted`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <li data-testid={`admin-customer-${c.id}`} className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-brand-ink">
            {c.name} {c.is_test && <span className="text-[10px] font-bold bg-brand-red text-white px-1.5 py-0.5 rounded-sm uppercase align-middle">Test</span>}
          </p>
          <p className="text-xs text-zinc-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <span className="text-xs font-bold px-2 py-0.5 rounded-sm bg-brand-ice/30 text-blue-900 uppercase tracking-wide">
              {ACCOUNT_TYPE_LABELS[c.account_type] || c.account_type}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-sm ${c.plan_status === "none" ? "bg-zinc-100 text-zinc-600" : "bg-green-100 text-green-800"}`}>
              {PLAN_LABELS[c.plan_status] || c.plan_status}{c.plan_renewal ? ` · renews ${c.plan_renewal}` : ""}
            </span>
            {!c.has_password && <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800">No password yet</span>}
          </div>
          {c.locations.length > 0 && (
            <p className="text-xs text-zinc-500 mt-1">{c.locations.map((l) => l.label).join(", ")}</p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button type="button" onClick={invite} disabled={busy} data-testid={`admin-customer-invite-${c.id}`}
            className="text-xs font-bold text-brand hover:text-brand-dark flex items-center gap-1 disabled:opacity-50">
            <MailPlus className="w-3.5 h-3.5" /> Invite
          </button>
          <button type="button" onClick={() => setEditing(!editing)} data-testid={`admin-customer-edit-${c.id}`}
            className="text-xs font-bold text-zinc-600 hover:text-brand flex items-center gap-1">
            <Pencil className="w-3.5 h-3.5" /> {editing ? "Close" : "Edit"}
          </button>
          <button type="button" onClick={remove} disabled={busy} data-testid={`admin-customer-delete-${c.id}`}
            className="text-xs font-bold text-brand-red hover:text-red-700 flex items-center gap-1 disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
      {editing && (
        <div className="mt-3">
          <CustomerEditor initial={c} onSubmit={save} submitLabel="Save Customer" busy={busy} />
        </div>
      )}
    </li>
  );
};

const DeleteTestDataButton = ({ onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Delete ALL test accounts, their visit reports and service records? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/test-data/delete");
      toast.success(`Deleted ${r.data.users} test account(s), ${r.data.visit_reports} report(s), ${r.data.service_records} record(s)`);
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid="admin-delete-test-data-btn"
      className="text-xs font-bold text-brand-red border border-brand-red hover:bg-brand-red hover:text-white px-3 py-1.5 rounded-sm flex items-center gap-1.5 disabled:opacity-50 transition-colors duration-200">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete All TEST Data
    </button>
  );
};

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => api.get("/admin/customers").then((r) => setCustomers(r.data.customers)).catch(() => setCustomers([]));
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    setCreating(true);
    try {
      await api.post("/admin/customers", payload);
      toast.success("Customer created — click Invite to send them their password link");
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const shown = customers?.filter((c) => {
    const q = query.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  }) || [];

  return (
    <div data-testid="admin-customers-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-heading font-bold text-lg text-brand-ink flex items-center gap-2">
          <Users className="w-5 h-5 text-brand" /> Customers (Admin)
        </h2>
        <DeleteTestDataButton onDeleted={load} />
      </div>
      <p className="text-sm text-zinc-600 mb-4">Add residential, business, condo resident or association customers, set their plan and locations, then invite them to set a password.</p>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="admin-customers-search-input" placeholder="Search customers..." value={query}
            onChange={(e) => setQuery(e.target.value)} className="rounded-sm pl-9" />
        </div>
        <button type="button" onClick={() => setShowCreate(!showCreate)} data-testid="admin-customer-new-btn"
          className="bg-brand hover:bg-brand-dark text-white font-bold px-4 rounded-sm text-sm shrink-0 transition-colors duration-200">
          {showCreate ? "Close" : "+ New Customer"}
        </button>
      </div>
      {showCreate && (
        <div className="mb-4">
          <CustomerEditor key="create" onSubmit={create} submitLabel="Create Customer" busy={creating} />
        </div>
      )}
      {customers === null && <Loader2 className="w-6 h-6 text-brand animate-spin" />}
      {customers !== null && shown.length === 0 && <p data-testid="admin-customers-empty" className="text-sm text-zinc-600">No customers found.</p>}
      {shown.length > 0 && (
        <ul className="divide-y divide-zinc-100">
          {shown.map((c) => <CustomerRow key={c.id} customer={c} onChanged={load} />)}
        </ul>
      )}
    </div>
  );
};
